// ============================================================
// roomCollisionGrid.ts — 3-Zone Polygon Collision System
// ============================================================
//
// APPROVED v7 DESIGN — LOCKED. Do not modify zone geometry.
//
// Three collision zones per room:
//
//   RED   — Wall panels + corner clip triangles. ALWAYS blocked.
//   BLUE  — Center cross + tapered trapezoid doorways. ALWAYS walkable.
//   GREEN — 4 curved corner zones with inward-bowing quarter-circle arcs.
//           Walkable when player is in this room.
//           When player is NOT in this room, entities in green zones
//           get redirected toward the nearest blue doorway.
//
// Doorways are tapered trapezoids (wider at room edge, narrower at
// wall inner edge) matching the actual alpha-channel room art.
//
// Corner clips are 48px right triangles at each inner wall corner,
// drawn ON TOP of the green corners (always RED / blocked).
//
// ============================================================

export const ROOM_SIZE_PX = 768;

// ── Wall thickness ──
const WALL = 96;
const OUTER = 672; // 768 - 96

// ── Corner clip size ──
const CLIP = 48;

// ── Doorway coordinates (from alpha-channel scan of room art) ──
// N/S doors: wider at room edge (OUT), narrower at wall inner edge (IN)
const N_OUT_L = 280, N_OUT_R = 488;
const N_IN_L  = 300, N_IN_R  = 468;
const S_OUT_L = 280, S_OUT_R = 488;
const S_IN_L  = 300, S_IN_R  = 468;
// E/W doors:
const E_OUT_T = 300, E_OUT_B = 468;
const E_IN_T  = 316, E_IN_B  = 452;
const W_OUT_T = 300, W_OUT_B = 468;
const W_IN_T  = 316, W_IN_B  = 452;

// ── Blue cross boundaries (inner walkable rectangle) ──
const CROSS_LEFT   = 300;
const CROSS_RIGHT  = 468;
const CROSS_TOP    = 316;
const CROSS_BOTTOM = 452;

// ── Export constants for other modules ──
export {
  WALL, OUTER, CLIP,
  N_OUT_L, N_OUT_R, N_IN_L, N_IN_R,
  S_OUT_L, S_OUT_R, S_IN_L, S_IN_R,
  E_OUT_T, E_OUT_B, E_IN_T, E_IN_B,
  W_OUT_T, W_OUT_B, W_IN_T, W_IN_B,
  CROSS_LEFT, CROSS_RIGHT, CROSS_TOP, CROSS_BOTTOM,
};

// ============================================================
// Zone Classification
// ============================================================

export type CollisionZone = 'red' | 'blue' | 'green';

/**
 * Classify a local room coordinate (0..767, 0..767) into a collision zone.
 *
 * Priority order:
 *   1. Out of bounds → 'red'
 *   2. In a blue doorway trapezoid → 'blue'
 *   3. In the blue center cross → 'blue'
 *   4. In a red corner clip triangle → 'red'
 *   5. In a green curved corner → 'green'
 *   6. In a red wall panel → 'red'
 *   7. Fallback (inside room interior) → 'blue'
 */
export function classifyPoint(lx: number, ly: number): CollisionZone {
  // Out of room bounds
  if (lx < 0 || lx >= ROOM_SIZE_PX || ly < 0 || ly >= ROOM_SIZE_PX) return 'red';

  // ── BLUE: Tapered trapezoid doorways ──
  // North doorway: trapezoid from (N_OUT_L,0)→(N_OUT_R,0) to (N_IN_L,WALL)→(N_IN_R,WALL)
  if (ly >= 0 && ly <= WALL) {
    const t = ly / WALL; // 0 at room edge, 1 at wall inner edge
    const leftEdge  = N_OUT_L + (N_IN_L - N_OUT_L) * t;
    const rightEdge = N_OUT_R + (N_IN_R - N_OUT_R) * t;
    if (lx >= leftEdge && lx <= rightEdge) return 'blue';
  }
  // South doorway
  if (ly >= OUTER && ly < ROOM_SIZE_PX) {
    const t = (ly - OUTER) / WALL; // 0 at wall inner edge, 1 at room edge
    const leftEdge  = S_IN_L + (S_OUT_L - S_IN_L) * t;
    const rightEdge = S_IN_R + (S_OUT_R - S_IN_R) * t;
    if (lx >= leftEdge && lx <= rightEdge) return 'blue';
  }
  // West doorway
  if (lx >= 0 && lx <= WALL) {
    const t = lx / WALL; // 0 at room edge, 1 at wall inner edge
    const topEdge    = W_OUT_T + (W_IN_T - W_OUT_T) * t;
    const bottomEdge = W_OUT_B + (W_IN_B - W_OUT_B) * t;
    if (ly >= topEdge && ly <= bottomEdge) return 'blue';
  }
  // East doorway
  if (lx >= OUTER && lx < ROOM_SIZE_PX) {
    const t = (lx - OUTER) / WALL; // 0 at wall inner edge, 1 at room edge
    const topEdge    = E_IN_T + (E_OUT_T - E_IN_T) * t;
    const bottomEdge = E_IN_B + (E_OUT_B - E_IN_B) * t;
    if (ly >= topEdge && ly <= bottomEdge) return 'blue';
  }

  // ── BLUE: Center cross ──
  // Horizontal bar: full width of interior, height = CROSS_TOP..CROSS_BOTTOM
  if (lx >= WALL && lx <= OUTER && ly >= CROSS_TOP && ly <= CROSS_BOTTOM) return 'blue';
  // Vertical bar: full height of interior, width = CROSS_LEFT..CROSS_RIGHT
  if (ly >= WALL && ly <= OUTER && lx >= CROSS_LEFT && lx <= CROSS_RIGHT) return 'blue';

  // ── RED CORNER CLIP TRIANGLES — REMOVED IN BETA 15.40 ──
  // Per Dylan: "the only thing to stay are what is making the actuall
  // room walls a collisioin and the doorways walkable and the floor
  // walkable." The 48wu right-triangles at each inner wall corner were
  // an extra collision band beyond the rectangular wall. Removed so
  // the room collision is JUST the wall band + doorway gaps + floor.
  // Real pathfinding (navGraph A* + flowField) handles steering.

  // ── GREEN INTERIOR CORNER ZONES — REMOVED IN BETA 15.38 ──
  // Per Dylan: "magnet is a bandaid... what if we removed the inside green
  // curve trangles and instead in each corner of the room have a real
  // pathfinding engine forcing enemies to exit doors and not just running
  // into the corners of the rooms?"
  //
  // Confirmed via audit (enemySystem.ts line 1010-1020): the green-zone
  // redirect was OVERRIDING the navGraph A* waypoint in interior corners,
  // forcing enemies to walk to the bandaid's chosen doorway instead of
  // following the path the real pathfinder computed. The cleaner architecture
  // is to TRUST the real pathfinders:
  //   - navGraph A* (`findPath`) handles cross-room/inside-room navigation
  //     via 5 nodes/room (CENTER + 4 doorways) — already wired in BETA 15.3
  //   - flowField handles open-ground / outside-rooms steering with infinite
  //     wall cost so flow naturally routes through doorways
  //   - getDoorwayMagnet stays — pulls toward doorway center, ALIGNED with
  //     navGraph waypoints (not against), so it's a complement not a bandaid
  //
  // Result of removing the 4 quarter-ellipse blocks: interior corners now
  // classify as 'blue' (walkable, no redirect). Real pathfinding takes over
  // the steering at corners. If enemies still pile up there, it means
  // navGraph or flowField has a bug — and we fix THAT, not paper over it.
  //
  // The exterior green curves (convex / straight / diagonal_bridge in
  // generateExteriorGreenCurves) are UNTOUCHED — they're polygon-in-point
  // tested separately and continue to block enemy entry to outside-room
  // pockets between clusters.

  // ── RED: Wall panels (anything in the wall band not already classified) ──
  if (lx < WALL || lx > OUTER || ly < WALL || ly > OUTER) return 'red';

  // ── Fallback: inside room interior = blue ──
  return 'blue';
}

// ============================================================
// Legacy API (backward compatible)
// ============================================================

/** Check if a LOCAL room coordinate (0..767) is a wall (RED zone) */
export function isRoomWall(localX: number, localY: number): boolean {
  return classifyPoint(localX, localY) === 'red';
}

/** Check if a LOCAL room coordinate is in a GREEN zone */
export function isRoomGreen(localX: number, localY: number): boolean {
  return classifyPoint(localX, localY) === 'green';
}

/** Check if a LOCAL room coordinate is in a BLUE zone (always walkable) */
export function isRoomBlue(localX: number, localY: number): boolean {
  return classifyPoint(localX, localY) === 'blue';
}

// ============================================================
// Green Zone Redirect — find nearest blue doorway midpoint
// ============================================================

/** Doorway midpoints in local room coordinates (center of each doorway opening) */
const DOORWAY_MIDPOINTS = [
  { x: (N_OUT_L + N_OUT_R) / 2, y: 0,            dir: 'north' as const },  // N door
  { x: (S_OUT_L + S_OUT_R) / 2, y: ROOM_SIZE_PX,  dir: 'south' as const },  // S door
  { x: 0,            y: (W_OUT_T + W_OUT_B) / 2,  dir: 'west'  as const },  // W door
  { x: ROOM_SIZE_PX,  y: (E_OUT_T + E_OUT_B) / 2,  dir: 'east'  as const },  // E door
];

/**
 * For a point in a GREEN zone, find the nearest doorway midpoint
 * to redirect the entity toward. Returns world coordinates.
 */
export function getGreenRedirectTarget(
  localX: number, localY: number,
  roomWorldX: number, roomWorldY: number,
): { x: number; y: number } {
  let bestDist = Infinity;
  let bestTarget = { x: roomWorldX + ROOM_SIZE_PX / 2, y: roomWorldY + ROOM_SIZE_PX / 2 };

  for (const door of DOORWAY_MIDPOINTS) {
    const dx = localX - door.x;
    const dy = localY - door.y;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      bestTarget = { x: roomWorldX + door.x, y: roomWorldY + door.y };
    }
  }

  return bestTarget;
}

// ============================================================
// Doorway Approach Magnets
// ============================================================
// When an entity is approaching a room from outside (or leaving),
// its straight-line path often clips the wall corner next to a doorway.
// This function detects when the entity's path toward its target
// crosses through a room wall, and returns a waypoint at the doorway
// center so the entity steers through the opening instead of hitting
// the corner.
//
// The magnet activates when:
//   1. Entity is within MAGNET_RANGE of a room edge
//   2. Entity's path toward target would cross through a wall
//   3. There's a doorway on the relevant wall that could be used

const MAGNET_RANGE = 250; // How far from a room edge the magnet activates
const MAGNET_INNER = 48;  // How far inside the doorway the waypoint is placed

/** Inner doorway midpoints — placed slightly inside the room for smoother steering */
const DOORWAY_INNER_MIDPOINTS = [
  { x: (N_IN_L + N_IN_R) / 2, y: WALL + MAGNET_INNER,           dir: 'north' as const },
  { x: (S_IN_L + S_IN_R) / 2, y: OUTER - MAGNET_INNER,          dir: 'south' as const },
  { x: WALL + MAGNET_INNER,           y: (W_IN_T + W_IN_B) / 2, dir: 'west'  as const },
  { x: OUTER - MAGNET_INNER,          y: (E_IN_T + E_IN_B) / 2, dir: 'east'  as const },
];

export { DOORWAY_INNER_MIDPOINTS };

/**
 * Check if an entity at (ex, ey) heading toward (tx, ty) should be
 * steered through a doorway instead of beelining into a wall corner.
 *
 * Returns a waypoint (world coords) at the nearest doorway center
 * if the entity's path would clip a wall corner. Returns null if
 * no magnet is needed (entity can reach target without wall collision).
 *
 * Call this BEFORE normal pathfinding — if it returns a waypoint,
 * use that as the entity's immediate nav target.
 */
export function getDoorwayMagnet(
  rooms: ReadonlyArray<{ x: number; y: number; size: number; gaps: ReadonlyArray<{ x: number; y: number; w: number; h: number; dir: string }> }>,
  ex: number, ey: number,
  tx: number, ty: number,
): { x: number; y: number } | null {
  // Direction from entity to target
  const dx = tx - ex;
  const dy = ty - ey;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) return null;

  for (const room of rooms) {
    const rx = room.x;
    const ry = room.y;
    const rs = room.size;

    // Check if entity is near this room
    const nearX = ex >= rx - MAGNET_RANGE && ex <= rx + rs + MAGNET_RANGE;
    const nearY = ey >= ry - MAGNET_RANGE && ey <= ry + rs + MAGNET_RANGE;
    if (!nearX || !nearY) continue;

    // For each gap (doorway) in this room, check if the entity should
    // be steered toward it
    for (const gap of room.gaps) {
      const gapCenterX = gap.x + gap.w / 2;
      const gapCenterY = gap.y + gap.h / 2;

      // Find the matching inner midpoint for this gap direction
      const innerMid = DOORWAY_INNER_MIDPOINTS.find(d => d.dir === gap.dir);
      if (!innerMid) continue;

      const doorWorldX = rx + innerMid.x;
      const doorWorldY = ry + innerMid.y;

      // Check if entity is approaching this doorway from outside
      // and its path would clip the wall corner
      const isOutsideRoom = ex < rx || ex > rx + rs || ey < ry || ey > ry + rs;
      const targetInsideRoom = tx >= rx && tx <= rx + rs && ty >= ry && ty <= ry + rs;
      const entityInsideRoom = ex >= rx && ex <= rx + rs && ey >= ry && ey <= ry + rs;
      const targetOutsideRoom = tx < rx || tx > rx + rs || ty < ry || ty > ry + rs;

      // Case 1: Entity outside, target inside — needs to enter through doorway
      // Case 2: Entity inside, target outside — needs to exit through doorway
      const needsDoorway = (isOutsideRoom && targetInsideRoom) ||
                           (entityInsideRoom && targetOutsideRoom);
      if (!needsDoorway) continue;

      // Check if this is the RIGHT doorway for the entity's approach direction
      const entityToDoorDx = doorWorldX - ex;
      const entityToDoorDy = doorWorldY - ey;
      const entityToDoorDist = Math.sqrt(entityToDoorDx * entityToDoorDx + entityToDoorDy * entityToDoorDy);
      if (entityToDoorDist > MAGNET_RANGE * 2) continue;

      // Check if the entity's movement direction roughly aligns with
      // the direction toward this doorway (dot product > 0)
      const dot = (dx / dist) * (entityToDoorDx / entityToDoorDist) +
                  (dy / dist) * (entityToDoorDy / entityToDoorDist);
      if (dot < 0.2) continue; // Entity is moving away from this doorway

      // Check if the straight line from entity to target would miss the doorway
      // by computing the closest point on the line to the doorway center
      const t = Math.max(0, Math.min(1,
        ((doorWorldX - ex) * dx + (doorWorldY - ey) * dy) / (dist * dist)
      ));
      const closestX = ex + dx * t;
      const closestY = ey + dy * t;
      const missDistSq = (closestX - doorWorldX) ** 2 + (closestY - doorWorldY) ** 2;

      // If the line passes close to the doorway center, no magnet needed
      // (entity will naturally go through the doorway)
      const doorHalfWidth = Math.max(gap.w, gap.h) / 2;
      if (missDistSq < doorHalfWidth * doorHalfWidth) continue;

      // Entity's path misses the doorway — activate magnet!
      // Return the doorway center as a waypoint
      return { x: doorWorldX, y: doorWorldY };
    }
  }

  return null;
}

// ============================================================
// Exterior Green Curves — Runtime generation
// ============================================================
// These are generated at map init time based on room neighbor topology.
// Each curve is a convex polygon (fan shape) in WORLD coordinates.
// Entities inside an exterior green curve get redirected toward the
// nearest doorway of the closest room.

export interface ExteriorGreenCurve {
  /** World-space polygon vertices (fan: center + arc points) */
  polygon: Array<{ x: number; y: number }>;
  /** The room this curve is associated with (for redirect targeting) */
  roomWorldX: number;
  roomWorldY: number;
  /** Which corner/edge this curve covers */
  type: 'convex' | 'concave' | 'straight' | 'diagonal_bridge';
}

/**
 * Generate a quadratic bezier arc between two points with a control point.
 */
function bezierArc(
  p1x: number, p1y: number,
  p2x: number, p2y: number,
  cpx: number, cpy: number,
  steps: number = 64,
): Array<{ x: number; y: number }> {
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const mt = 1 - t;
    pts.push({
      x: mt * mt * p1x + 2 * mt * t * cpx + t * t * p2x,
      y: mt * mt * p1y + 2 * mt * t * cpy + t * t * p2y,
    });
  }
  return pts;
}

/** Bow distance for exterior curves — larger = more outward bulge + more coverage for bot redirect */
const BOW = 300;
/** Bow distance for diagonal bridge curves */
const DIAG_BOW = 350;

/**
 * Point-in-convex-polygon test (works for any simple polygon).
 * Uses ray-casting algorithm.
 */
export function pointInPolygon(
  px: number, py: number,
  poly: ReadonlyArray<{ x: number; y: number }>,
): boolean {
  let inside = false;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Generate all exterior green curves for the entire map.
 * Call once after room placement.
 *
 * @param rooms - Array of { x, y, size } for all placed rooms
 * @param roomSize - Size of each room (768)
 * @returns Array of ExteriorGreenCurve polygons
 */
export function generateExteriorGreenCurves(
  rooms: ReadonlyArray<{ x: number; y: number; size: number }>,
  roomSize: number,
): ExteriorGreenCurve[] {
  // BETA 15.44 — Per Dylan (with screenshot): "major issue. with stuck. is
  // the 90 degree rooms that connect like this. the dianal room touch issue."
  // Selectively re-enabling the `diagonal_bridge` curves ONLY — the other 3
  // types (convex / straight / concave) STAY removed. Diagonal-touching rooms
  // create a corner pinch point where enemies pile up because the flowField
  // gets confused near the convex junction; the bridge polygons block that
  // pinch and force enemies to route through actual doorways.
  //
  // 15.39 left a clean revert path for exactly this case. We're using it
  // surgically — not restoring all curves, just the diagonal_bridge type.
  //
  // If the bot run shows this still doesn't fix it, the next step is real
  // pathfinding work (navGraph nodes at corner junctions), not more curves.

  const RS_BR = roomSize;
  const curves: ExteriorGreenCurve[] = [];

  // Build a grid lookup for O(1) neighbor checks
  const roomGrid_BR = new Set<string>();
  for (const room of rooms) {
    const gx = Math.round(room.x / RS_BR);
    const gy = Math.round(room.y / RS_BR);
    roomGrid_BR.add(`${gx},${gy}`);
  }
  const has_BR = (gx: number, gy: number) => roomGrid_BR.has(`${gx},${gy}`);

  for (const room of rooms) {
    const gx = Math.round(room.x / RS_BR);
    const gy = Math.round(room.y / RS_BR);
    const ox = room.x;
    const oy = room.y;

    const n = has_BR(gx, gy - 1);
    const s = has_BR(gx, gy + 1);
    const e = has_BR(gx + 1, gy);
    const w = has_BR(gx - 1, gy);

    // ── DIAGONAL BRIDGE CURVES (re-enabled in 15.44) ──
    // Only fire when two rooms are diagonally adjacent AND there's no room
    // bridging them (i.e. neither cardinal neighbor exists). Each diagonal
    // gets two bridge polygons connecting the nearest doorway pair.

    // SE diagonal: room at (gx+1, gy+1), no room at (gx+1,gy) or (gx,gy+1)
    if (has_BR(gx + 1, gy + 1) && !e && !s) {
      {
        const p1x = ox + S_OUT_R, p1y = oy + RS_BR;
        const p2x = (gx + 1) * RS_BR, p2y = (gy + 1) * RS_BR + W_OUT_T;
        const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
        const cpx = mx - DIAG_BOW * 0.3, cpy = my + DIAG_BOW * 0.3;
        const arc = bezierArc(p1x, p1y, p2x, p2y, cpx, cpy);
        curves.push({
          polygon: [{ x: ox + RS_BR, y: oy + RS_BR }, ...arc],
          roomWorldX: ox, roomWorldY: oy,
          type: 'diagonal_bridge',
        });
      }
      {
        const p1x = ox + RS_BR, p1y = oy + E_OUT_B;
        const p2x = (gx + 1) * RS_BR + N_OUT_L, p2y = (gy + 1) * RS_BR;
        const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
        const cpx = mx + DIAG_BOW * 0.3, cpy = my - DIAG_BOW * 0.3;
        const arc = bezierArc(p1x, p1y, p2x, p2y, cpx, cpy);
        curves.push({
          polygon: [{ x: (gx + 1) * RS_BR, y: (gy + 1) * RS_BR }, ...arc],
          roomWorldX: (gx + 1) * RS_BR, roomWorldY: (gy + 1) * RS_BR,
          type: 'diagonal_bridge',
        });
      }
    }

    // NE diagonal: room at (gx+1, gy-1), no room at (gx+1,gy) or (gx,gy-1)
    if (has_BR(gx + 1, gy - 1) && !e && !n) {
      {
        const p1x = ox + N_OUT_R, p1y = oy;
        const p2x = (gx + 1) * RS_BR, p2y = (gy - 1) * RS_BR + W_OUT_B;
        const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
        const cpx = mx - DIAG_BOW * 0.3, cpy = my - DIAG_BOW * 0.3;
        const arc = bezierArc(p1x, p1y, p2x, p2y, cpx, cpy);
        curves.push({
          polygon: [{ x: ox + RS_BR, y: oy }, ...arc],
          roomWorldX: ox, roomWorldY: oy,
          type: 'diagonal_bridge',
        });
      }
      {
        const p1x = ox + RS_BR, p1y = oy + E_OUT_T;
        const p2x = (gx + 1) * RS_BR + S_OUT_L, p2y = (gy - 1) * RS_BR + RS_BR;
        const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
        const cpx = mx + DIAG_BOW * 0.3, cpy = my + DIAG_BOW * 0.3;
        const arc = bezierArc(p1x, p1y, p2x, p2y, cpx, cpy);
        curves.push({
          polygon: [{ x: (gx + 1) * RS_BR, y: (gy - 1) * RS_BR + RS_BR }, ...arc],
          roomWorldX: (gx + 1) * RS_BR, roomWorldY: (gy - 1) * RS_BR,
          type: 'diagonal_bridge',
        });
      }
    }

    // SW diagonal: room at (gx-1, gy+1), no room at (gx-1,gy) or (gx,gy+1)
    if (has_BR(gx - 1, gy + 1) && !w && !s) {
      {
        const p1x = ox + S_OUT_L, p1y = oy + RS_BR;
        const p2x = (gx - 1) * RS_BR + RS_BR, p2y = (gy + 1) * RS_BR + E_OUT_T;
        const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
        const cpx = mx + DIAG_BOW * 0.3, cpy = my + DIAG_BOW * 0.3;
        const arc = bezierArc(p1x, p1y, p2x, p2y, cpx, cpy);
        curves.push({
          polygon: [{ x: ox, y: oy + RS_BR }, ...arc],
          roomWorldX: ox, roomWorldY: oy,
          type: 'diagonal_bridge',
        });
      }
      {
        const p1x = ox, p1y = oy + W_OUT_B;
        const p2x = (gx - 1) * RS_BR + N_OUT_R, p2y = (gy + 1) * RS_BR;
        const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
        const cpx = mx - DIAG_BOW * 0.3, cpy = my - DIAG_BOW * 0.3;
        const arc = bezierArc(p1x, p1y, p2x, p2y, cpx, cpy);
        curves.push({
          polygon: [{ x: (gx - 1) * RS_BR + RS_BR, y: (gy + 1) * RS_BR }, ...arc],
          roomWorldX: (gx - 1) * RS_BR, roomWorldY: (gy + 1) * RS_BR,
          type: 'diagonal_bridge',
        });
      }
    }

    // NW diagonal: room at (gx-1, gy-1), no room at (gx-1,gy) or (gx,gy-1)
    if (has_BR(gx - 1, gy - 1) && !w && !n) {
      {
        const p1x = ox + N_OUT_L, p1y = oy;
        const p2x = (gx - 1) * RS_BR + RS_BR, p2y = (gy - 1) * RS_BR + E_OUT_B;
        const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
        const cpx = mx + DIAG_BOW * 0.3, cpy = my - DIAG_BOW * 0.3;
        const arc = bezierArc(p1x, p1y, p2x, p2y, cpx, cpy);
        curves.push({
          polygon: [{ x: ox, y: oy }, ...arc],
          roomWorldX: ox, roomWorldY: oy,
          type: 'diagonal_bridge',
        });
      }
      {
        const p1x = ox, p1y = oy + W_OUT_T;
        const p2x = (gx - 1) * RS_BR + S_OUT_R, p2y = (gy - 1) * RS_BR + RS_BR;
        const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
        const cpx = mx - DIAG_BOW * 0.3, cpy = my + DIAG_BOW * 0.3;
        const arc = bezierArc(p1x, p1y, p2x, p2y, cpx, cpy);
        curves.push({
          polygon: [{ x: (gx - 1) * RS_BR + RS_BR, y: (gy - 1) * RS_BR + RS_BR }, ...arc],
          roomWorldX: (gx - 1) * RS_BR, roomWorldY: (gy - 1) * RS_BR,
          type: 'diagonal_bridge',
        });
      }
    }
  }

  return curves;
}

// ============================================================
// Debug display grid (backward compatible)
// ============================================================

export const ROOM_COLLISION_GRID_RES = 8;
export const ROOM_COLLISION_GRID_SIZE = 96;

function _buildDisplayGrid(): string[] {
  const grid: string[] = [];
  for (let gy = 0; gy < ROOM_COLLISION_GRID_SIZE; gy++) {
    let row = '';
    for (let gx = 0; gx < ROOM_COLLISION_GRID_SIZE; gx++) {
      const px = gx * ROOM_COLLISION_GRID_RES + ROOM_COLLISION_GRID_RES / 2;
      const py = gy * ROOM_COLLISION_GRID_RES + ROOM_COLLISION_GRID_RES / 2;
      row += isRoomWall(px, py) ? '1' : '0';
    }
    grid.push(row);
  }
  return grid;
}

export const ROOM_COLLISION_GRID_DISPLAY = _buildDisplayGrid();

// ── Legacy polygon exports for debug overlay ──
const WALKABLE_POLY: ReadonlyArray<{ x: number; y: number }> = [
  { x: WALL + CLIP, y: WALL },
  { x: OUTER - CLIP, y: WALL },
  { x: OUTER, y: WALL + CLIP },
  { x: OUTER, y: OUTER - CLIP },
  { x: OUTER - CLIP, y: OUTER },
  { x: WALL + CLIP, y: OUTER },
  { x: WALL, y: OUTER - CLIP },
  { x: WALL, y: WALL + CLIP },
];

export const COLLISION_POLYGON = WALKABLE_POLY;

const DOOR_N = { xMin: N_OUT_L, xMax: N_OUT_R, yMin: 0, yMax: WALL };
const DOOR_S = { xMin: S_OUT_L, xMax: S_OUT_R, yMin: OUTER, yMax: ROOM_SIZE_PX };
const DOOR_W = { xMin: 0, xMax: WALL, yMin: W_OUT_T, yMax: W_OUT_B };
const DOOR_E = { xMin: OUTER, xMax: ROOM_SIZE_PX, yMin: E_OUT_T, yMax: E_OUT_B };

export const DOORWAY_ZONES = [DOOR_N, DOOR_E, DOOR_S, DOOR_W];
